import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { controlMatrixApi, raciApi, peopleApi, api } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { toneFromMap, type Tone } from '../constants/tone';
import { maturityToScore, isImplementationComplete } from '../utils/controlConformity';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type Scope = 'In Scope' | 'Out of Scope' | 'Not Applicable' | '';

interface Control {
  controlId: string;
  matrixId: string;
  fields?: Record<string, any>;
}

interface RaciAssignment {
  raciId: string;
  controlId?: string;
  artefactId?: string;
  personId: string;
  role: 'R' | 'A' | 'C' | 'I';
}

interface Person {
  personId: string;
  personName?: string;
  firstName?: string;
  lastName?: string;
}

interface Matrix {
  matrixId: string;
  name?: string;
  matrixName?: string;
}

type SortField = 'controlId' | 'title' | 'matrixName' | 'status' | 'framework' | 'scope' | 'implementationStatus' | 'responsible' | 'accountable';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getField = (control: Control, key: string): string => {
  const val = control.fields?.[key];
  if (!val) return '';
  if (typeof val === 'object' && 'value' in val) return String(val.value ?? '');
  return String(val);
};

const getPersonName = (p: Person): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return full || p.personName || p.personId;
};

const SCOPE_TONE: Record<string, Tone> = {
  'In Scope': 'success',
  'Out of Scope': 'danger',
  'Not Applicable': 'neutral',
};
const scopeChipTone = (scope: string) => toneFromMap(SCOPE_TONE, scope, 'warning');

const MATRIX_STATUS_TONE: Record<string, Tone> = {
  Active: 'success',
  Draft: 'warning',
};

const IMPL_STATUS_TONE: Record<string, Tone> = {
  Completed: 'success',
  'In-progress': 'primary',
  Planning: 'warning',
  'On-hold': 'danger',
};

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------
const exportCSV = (rows: any[]) => {
  const headers = [
    'Control ID', 'Control Title', 'Matrix', 'Status', 'Framework',
    'Assigned Scope', 'Justification for Inclusion', 'Justification for Exclusion',
    'Implementation Status', 'Responsible', 'Accountable',
  ];
  const esc = (v: string) => `"${(v || '').replace(/"/g, '""').replace(/<[^>]+>/g, '')}"`;
  const csv = [
    headers.map((h) => `"${h}"`).join(','),
    ...rows.map((r) => [
      esc(r.controlId), esc(r.title), esc(r.matrixName), esc(r.status), esc(r.framework),
      esc(r.scope), esc(r.inclJustification), esc(r.exclJustification),
      esc(r.implementationStatus), esc(r.responsible), esc(r.accountable),
    ].join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'statement_of_applicability.csv';
  a.click();
};

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------
const exportPDF = async (rows: any[], stats: any, setExporting: (v: boolean) => void) => {
  setExporting(true);
  try {
    const esc = (s?: string | null) => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const scopeStyle = (scope: string) => {
      if (scope === 'In Scope') return 'background:#dcfce7;color:#15803d;';
      if (scope === 'Out of Scope') return 'background:#fee2e2;color:#dc2626;';
      if (scope === 'Not Applicable') return 'background:#f3f4f6;color:#6b7280;';
      return 'background:#fef9c3;color:#854d0e;';
    };
    const implStyle = (impl: string) => {
      const s = (impl || '').toLowerCase();
      if (s === 'completed') return 'background:#dcfce7;color:#15803d;';
      if (s === 'in-progress') return 'background:#ede9fe;color:#5b21b6;';
      if (s === 'planning') return 'background:#fef9c3;color:#854d0e;';
      if (s === 'on-hold') return 'background:#ffedd5;color:#c2410c;';
      if (s === 'cancelled') return 'background:#fee2e2;color:#dc2626;';
      return 'background:#f3f4f6;color:#6b7280;';
    };
    const badge = (text: string, style: string) =>
      `<span style="${style}padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;white-space:nowrap;">${esc(text)}</span>`;
    const statCard = (label: string, value: number, color: string) =>
      `<div style="display:inline-block;width:calc(20% - 8px);margin:0 4px 8px 0;padding:10px 14px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;vertical-align:top;text-align:center;">` +
      `<span style="font-size:20px;font-weight:700;color:${color};">${value}</span><br>` +
      `<span style="font-size:11px;color:#6b7280;">${label}</span></div>`;

    const tableRows = rows.map((row, i) => {
      const justification = row.scope === 'Out of Scope'
        ? (row.exclJustification || '').replace(/<[^>]+>/g, '').substring(0, 120)
        : (row.inclJustification || '').substring(0, 120);
      const bg = i % 2 === 0 ? '#ffffff' : '#f9fafb';
      return `<tr style="background:${bg};border-bottom:1px solid #e5e7eb;">
        <td style="padding:8px;vertical-align:top;width:8%;"><strong style="color:#4f46e5;font-size:12px;">${esc(row.controlId)}</strong></td>
        <td style="padding:8px;vertical-align:top;width:22%;font-size:12px;color:#111827;font-weight:500;">${esc(row.title)}</td>
        <td style="padding:8px;vertical-align:top;width:12%;font-size:11px;color:#374151;">${esc(row.matrixName)}</td>
        <td style="padding:8px;vertical-align:top;width:10%;">${row.scope ? badge(row.scope, scopeStyle(row.scope)) : '<span style="color:#9ca3af;">—</span>'}</td>
        <td style="padding:8px;vertical-align:top;width:14%;font-size:11px;color:#374151;">${esc(justification) || '—'}</td>
        <td style="padding:8px;vertical-align:top;width:12%;">${row.implementationStatus ? badge(row.implementationStatus, implStyle(row.implementationStatus)) : '<span style="color:#9ca3af;">—</span>'}</td>
        <td style="padding:8px;vertical-align:top;width:11%;font-size:11px;color:#374151;">${esc(row.responsible)}</td>
        <td style="padding:8px;vertical-align:top;width:11%;font-size:11px;color:#374151;">${esc(row.accountable)}</td>
      </tr>`;
    }).join('');

    const genDateSoA = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;background:#ffffff;color:#111827;width:1000px;padding:40px;">
      <div style="padding:32px;background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">
        <h1 style="font-size:28px;font-weight:800;color:#111827;margin:0 0 12px 0;">Statement of Applicability</h1>
        <div style="margin-bottom:20px;">
          <span style="padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600;color:#5b21b6;background:#ede9fe;">ISO 27001:2022 \u2014 Annex A Control Applicability</span>
          <span style="font-size:13px;color:#6b7280;margin-left:12px;">Generated ${genDateSoA}</span>
        </div>
        ${statCard('Total Controls', stats.total, '#111827')}
        ${statCard('In Scope', stats.inScope, '#15803d')}
        ${statCard('Out of Scope', stats.outOfScope, '#dc2626')}
        ${statCard('Not Applicable', stats.notApplicable, '#6b7280')}
        ${statCard('Not Assessed', stats.notAssessed, '#d97706')}
      </div>
      <div style="border:1px solid #e5e7eb;border-radius:8px;margin-bottom:32px;">
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f9fafb;border-bottom:2px solid #e5e7eb;">
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Control ID</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Title</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Matrix</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Scope</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Justification</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Impl. Status</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Responsible</th>
              <th style="padding:10px 8px;text-align:left;font-size:10px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.06em;">Accountable</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-top:32px;"><tr>
        <td style="padding-top:16px;border-top:1px solid #e5e7eb;font-size:11px;color:#9ca3af;">Statement of Applicability \u00b7 ISO/IEC 27001:2022 Annex A</td>
        <td style="padding-top:16px;border-top:1px solid #e5e7eb;text-align:right;font-size:11px;color:#9ca3af;">Generated ${genDateSoA}</td>
      </tr></table>
    </div>`;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;top:99999px;left:0;width:1000px;';
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);
    void wrapper.getBoundingClientRect();
    const fullH = wrapper.scrollHeight;

    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(wrapper, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff', width: 1000, height: fullH, windowWidth: 1000, windowHeight: fullH });
    document.body.removeChild(wrapper);

    const margin = 10;
    const printW = 190;
    const imgH = canvas.height / 2;
    const ratio = printW / (canvas.width / 2);
    const docH = imgH * ratio + margin * 2;
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: [210, Math.max(docH, 210.1)] });
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, printW, imgH * ratio);
    pdf.save('statement_of_applicability.pdf');
  } catch (err) {
    console.error(err);
  } finally {
    setExporting(false);
  }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const StatementOfApplicabilityPage: React.FC = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filterMatrix, setFilterMatrix] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const bsTooltip = (window as any).bootstrap?.Tooltip;
      if (bsTooltip) {
        document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
          bsTooltip.getOrCreateInstance(el);
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  });

  // -- data fetching --

  const { data: matrices = [] } = useQuery<Matrix[]>({
    queryKey: ['controlMatrices'],
    queryFn: controlMatrixApi.getAll,
  });

  // Fetch controls for every matrix in parallel
  const controlQueries = useQueries({
    queries: matrices.map((m) => ({
      queryKey: ['controls-soa', m.matrixId],
      queryFn: async () => {
        const res = await api.get<{ controls: Control[] }>(`/controls?matrixId=${encodeURIComponent(m.matrixId)}`);
        return (res.controls || []).map((c) => ({ ...c, matrixId: m.matrixId }));
      },
      enabled: !!m.matrixId,
    })),
  });

  const allControls = useMemo<Control[]>(
    () => controlQueries.flatMap((q) => (q.data as Control[]) || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlQueries.map((q) => q.dataUpdatedAt).join(',')],
  );

  const { data: allRaci = [] } = useQuery<RaciAssignment[]>({
    queryKey: ['raci-all-controls-soa'],
    queryFn: raciApi.getAll,
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const personMap = useMemo(() => {
    const m = new Map<string, Person>();
    people.forEach((p) => m.set(p.personId, p));
    return m;
  }, [people]);

  const matrixMap = useMemo(() => {
    const m = new Map<string, string>();
    matrices.forEach((mx) => m.set(mx.matrixId, mx.matrixName || mx.name || mx.matrixId));
    return m;
  }, [matrices]);

  // Build enriched rows
  const rows = useMemo(() => {
    return allControls.map((c) => {
      const controlId = getField(c, 'control_id') || c.controlId;
      const title = getField(c, 'control_title') || '';
      const scope = getField(c, 'assigned_scope') as Scope;
      const inclJustification = getField(c, 'justification_for_inclusion');
      const exclJustification = getField(c, 'justification_for_exclusion');
      const status = getField(c, 'status');
      const framework = getField(c, 'framework');
      const implementationStatus = getField(c, 'implementation_status');
      const matrixName = matrixMap.get(c.matrixId) || c.matrixId;

      // RACI — match on controlId field in the record
      const raciForControl = allRaci.filter(
        (r: any) => r.controlId === c.controlId || r.artefactId === c.controlId,
      );
      const responsiblePeople = raciForControl
        .filter((r) => r.role === 'R')
        .map((r) => getPersonName(personMap.get(r.personId) || { personId: r.personId }))
        .filter(Boolean)
        .join(', ');
      const accountablePeople = raciForControl
        .filter((r) => r.role === 'A')
        .map((r) => getPersonName(personMap.get(r.personId) || { personId: r.personId }))
        .filter(Boolean)
        .join(', ');

      // Conformity risk — ISO-scoped in-scope controls that fail operating criteria. Reads
      // implementation_status/current_maturity via the same shared helpers ControlDetail.tsx's
      // own ISO Conformity banner uses, so a control is interpreted identically on both pages -
      // see controlConformity.ts for why a plain includes('complet')/charAt(0) parse doesn't
      // work against the real stored values ('Implemented'/'not_implemented', bare maturity
      // labels like 'Managed'/'Unassessed' with no leading digit).
      const isIsoScoped = /iso/i.test(framework) && scope !== 'Out of Scope' && scope !== 'Not Applicable';
      const hasRaciOwnership = raciForControl.some((r) => r.role === 'R' || r.role === 'A');
      const currentMaturityRaw = getField(c, 'current_maturity').trim();
      const linkedArtefactsCount = Array.isArray((c as any).linked_artefacts) ? (c as any).linked_artefacts.length : 0;
      const conformityReasons: string[] = [];
      if (isIsoScoped) {
        const implStatusLower = implementationStatus.toLowerCase();
        if (!implStatusLower.includes('not applicable') && !isImplementationComplete(implStatusLower)) {
          conformityReasons.push('Not fully implemented');
        }
        if (currentMaturityRaw !== '') {
          if (currentMaturityRaw.toLowerCase() === 'unassessed') {
            conformityReasons.push('Maturity not yet assessed');
          } else {
            const currentMaturityScore = maturityToScore(currentMaturityRaw);
            if (currentMaturityScore === null) conformityReasons.push('Maturity not yet assessed');
            else if (currentMaturityScore < 2) conformityReasons.push('Below minimum maturity (< Repeatable)');
          }
        }
        if (linkedArtefactsCount === 0) conformityReasons.push('No evidence linked');
        if (!hasRaciOwnership) conformityReasons.push('No RACI ownership (R or A)');
      }
      const conformityRisk = conformityReasons.length > 0;

      return {
        controlId,
        internalId: c.controlId,
        title,
        scope,
        inclJustification,
        exclJustification,
        status,
        framework,
        implementationStatus,
        matrixName,
        matrixId: c.matrixId,
        responsible: responsiblePeople,
        accountable: accountablePeople,
        conformityRisk,
        conformityReasons,
        isIsoScoped,
      };
    });
  }, [allControls, allRaci, personMap, matrixMap]);

  // Stats
  const stats = useMemo(() => ({
    total: rows.length,
    inScope: rows.filter((r) => r.scope === 'In Scope').length,
    outOfScope: rows.filter((r) => r.scope === 'Out of Scope').length,
    notApplicable: rows.filter((r) => r.scope === 'Not Applicable').length,
    notAssessed: rows.filter((r) => !r.scope).length,
    conformityRisk: rows.filter((r) => r.conformityRisk).length,
  }), [rows]);

  // Filtered
  const searched = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (!q || [r.controlId, r.title, r.matrixName, r.framework, r.status, r.implementationStatus, r.responsible, r.accountable]
        .some((v) => v?.toLowerCase().includes(q)))
      && (!filterMatrix || r.matrixId === filterMatrix)
      && (!filterScope || r.scope === filterScope),
    );
  }, [rows, search, filterMatrix, filterScope]);

  const { sorted: filtered, field: sortKey, dir: sortDir, toggle: handleSort } = useSortableData<typeof rows[number], SortField>(
    searched,
    (r, f) => r[f] ?? '',
    'controlId',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(filtered, 25);

  const isLoading = controlQueries.some((q) => q.isLoading) && allControls.length === 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-file-list-3-line"
        title="Statement of Applicability"
        colour="success"
        actions={
          <>
            <button
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => exportCSV(filtered)}
              disabled={filtered.length === 0}
            >
              <i className="ri-download-2-line"></i>CSV
            </button>
            <button
              className="np-btn np-btn-ghost np-btn-sm"
              onClick={() => exportPDF(filtered, stats, setExporting)}
              disabled={filtered.length === 0 || exporting}
            >
              {exporting ? <span className="spinner-border spinner-border-sm" /> : <i className="ri-file-pdf-line"></i>}
              {exporting ? 'Exporting…' : 'Export PDF'}
            </button>
          </>
        }
      />

      {/* Controls card */}
      <div className="np-card">
        <div className="np-card-head flex-wrap">
          <h2>Controls</h2>
          <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
            {stats.total > 0 && (
              <span className="np-chip np-tone-primary">{stats.total} total</span>
            )}
            {stats.inScope > 0 && (
              <span className="np-chip np-tone-success">{stats.inScope} In Scope</span>
            )}
            {stats.outOfScope > 0 && (
              <span className="np-chip np-tone-danger">{stats.outOfScope} Out of Scope</span>
            )}
            {stats.notApplicable > 0 && (
              <span className="np-chip np-tone-neutral">{stats.notApplicable} Not Applicable</span>
            )}
            {stats.notAssessed > 0 && (
              <span className="np-chip np-tone-warning">{stats.notAssessed} Not Assessed</span>
            )}
          </div>
        </div>
        <div className="np-card-body border-bottom">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <div className="np-search">
                <i className="ri-search-line"></i>
                <input
                  type="text"
                  placeholder="Search ID, title, owner…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button className="np-icon-btn" onClick={() => setSearch('')}>
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-6 col-md-3">
              <select
                className="np-select"
                value={filterMatrix}
                onChange={(e) => setFilterMatrix(e.target.value)}
              >
                <option value="">All Matrices</option>
                {matrices.map((m) => (
                  <option key={m.matrixId} value={m.matrixId}>
                    {m.matrixName || m.name || m.matrixId}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <select
                className="np-select"
                value={filterScope}
                onChange={(e) => setFilterScope(e.target.value)}
              >
                <option value="">All Scopes</option>
                <option value="In Scope">In Scope</option>
                <option value="Out of Scope">Out of Scope</option>
                <option value="Not Applicable">Not Applicable</option>
              </select>
            </div>
            <div className="col-12 col-md-2 d-flex align-items-center gap-2 justify-content-end">
              {(search || filterMatrix || filterScope) && (
                <button
                  className="np-btn np-btn-ghost np-btn-sm"
                  onClick={() => { setSearch(''); setFilterMatrix(''); setFilterScope(''); }}
                >
                  <i className="ri-filter-off-line"></i>Clear
                </button>
              )}
              <span className="np-sub np-mb-0">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>
        <div className="np-card-body p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <p className="np-sub mt-2 mb-0">Loading controls…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-file-list-3-line ri-3x d-block mb-2 opacity-25"></i>
              <p className="np-sub mb-0">
                {rows.length === 0
                  ? 'No controls found. Create controls in a Control Matrix first.'
                  : 'No rows match the current filters.'}
              </p>
            </div>
          ) : (
            <NimbusTable style={{ fontSize: '0.85rem' }} thead={<tr>
                  {([
                    { key: 'controlId',            label: 'Control ID',      w: 110 },
                    { key: 'title',                label: 'Control Title',   w: undefined },
                    { key: 'matrixName',           label: 'Matrix',          w: 130 },
                    { key: 'status',               label: 'Status',          w: 100 },
                    { key: 'framework',            label: 'Framework',       w: 130 },
                    { key: 'scope',                label: 'Scope',           w: 120 },
                    { key: '',                     label: 'Justification',   w: 140 },
                    { key: 'implementationStatus', label: 'Impl. Status',    w: 140 },
                    { key: 'responsible',          label: 'Responsible',     w: 120 },
                    { key: 'accountable',          label: 'Accountable',     w: 120 },
                  ] as { key: SortField | ''; label: string; w: number | undefined }[]).map(({ key, label, w }) => (
                    key ? (
                      <SortableTh key={label} field={key} active={sortKey === key} dir={sortDir} onSort={handleSort} width={w}>
                        {label}
                      </SortableTh>
                    ) : (
                      <th key={label} style={{ width: w }}>{label}</th>
                    )
                  ))}
                  <th style={{ width: 52 }}></th>
                </tr>}>
                {pageItems.map((row) => {
                  return (
                    <tr key={row.internalId}>
                      <td>
                        <code className="text-primary np-tabular" style={{ fontSize: '0.8rem' }}>{row.controlId || <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</code>
                      </td>
                      <td>
                        <span className="fw-semibold">{row.title || <span style={{ color: 'var(--bs-secondary-color)', fontStyle: 'italic' }}>Untitled</span>}</span>
                      </td>
                      <td>
                        <span className="np-chip np-tone-info text-truncate" style={{ maxWidth: 125 }} title={row.matrixName}>
                          {row.matrixName}
                        </span>
                      </td>
                      <td>
                        {row.status ? (
                          <span className={`np-chip np-tone-${toneFromMap(MATRIX_STATUS_TONE, row.status, 'neutral')}`}>{row.status}</span>
                        ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                      </td>
                      <td>
                        <span className="text-truncate d-block" style={{ maxWidth: 125 }} title={row.framework}>
                          {row.framework || <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                        </span>
                      </td>
                      <td>
                        {row.scope ? (
                          <span className={`np-chip np-tone-${scopeChipTone(row.scope)}`}>{row.scope}</span>
                        ) : (
                          <span className="np-chip np-tone-warning">Not Assessed</span>
                        )}
                      </td>
                      <td>
                        {row.scope === 'In Scope' && row.inclJustification ? (
                          <span className="np-chip np-tone-success">{row.inclJustification}</span>
                        ) : row.scope === 'Out of Scope' && row.exclJustification ? (
                          <span
                            className="text-truncate d-block"
                            style={{ maxWidth: 135, fontSize: '0.8rem' }}
                            title={row.exclJustification.replace(/<[^>]+>/g, '')}
                            dangerouslySetInnerHTML={{ __html: row.exclJustification }}
                          />
                        ) : (
                          <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>
                        )}
                      </td>
                      <td>
                        {row.implementationStatus ? (
                          <span className={`np-chip np-tone-${toneFromMap(IMPL_STATUS_TONE, row.implementationStatus, 'neutral')}`}>{row.implementationStatus}</span>
                        ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                      </td>
                      <td>
                        {row.responsible ? (
                          <span className="np-chip np-tone-primary">{row.responsible}</span>
                        ) : (
                          <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>
                        )}
                      </td>
                      <td>
                        {row.accountable ? (
                          <span className="np-chip np-tone-warning">{row.accountable}</span>
                        ) : (
                          <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="np-icon-btn"
                          title="Open control"
                          onClick={() => navigate(`/controls/${row.internalId}?matrixId=${row.matrixId}`)}
                        >
                          <i className="ri-external-link-line"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </NimbusTable>
          )}
          {pageItems.length > 0 && (
            <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
          )}
        </div>
      </div>
    </div>
  );
};
